import {
  format as formatAbsolute,
  formatRelative,
  isToday,
  type Locale,
} from 'date-fns'

export type DateFormat =
  | 'long-date'
  | 'medium-date'
  | 'medium-date-alt'
  | 'medium-date-alt-time'
  | 'medium-date-time'
  | 'short-date'
  | 'short-time'
  | 'iso-like'
  | 'iso-like-alt'
  | 'relative-time'
  | 'bidding-end'

const dateFormatMap: Record<DateFormat, string> = {
  'iso-like': 'yyyy:MM:dd HH:mm:ss', // 2024:11:06 13:34:00
  'iso-like-alt': 'yyyy-MM-dd HH:mm:ss', // 2024-11-06 13:34:00
  'long-date': 'MMMM d, y', // November 6, 2024
  'medium-date': 'MMM d, y', // Nov 6, 2024
  'medium-date-alt': 'dd MMM yyyy', // 06 Nov 2024
  'medium-date-alt-time': 'dd MMM yyyy • h:mm a', // 06 Nov 2024 • 1:34 PM
  'medium-date-time': 'MMM d, yyyy • h:mm a', // Nov 6, 2024 • 1:34 PM
  'relative-time': 'relative', // [last {day} | yesterday | today | tomorrow | next {day}] at h:mm a || MM/DD/YYYY
  'short-date': 'MM/dd/yyyy', // 11/06/2024
  'short-time': 'h:mm a', // 1:34 PM
  'bidding-end': 'bidding-end', // same day → "Today at h:mm a", else "MMM d, y"
}

export const formatDate = (
  date: Date | undefined,
  format: DateFormat,
  locale?: Locale,
  options?: { formatter?: (time: string) => string }
): string => {
  if (!date) {
    return 'N/A'
  }

  if (format === 'bidding-end') {
    if (isToday(date)) {
      const time = formatAbsolute(date, 'h:mm a', { locale })
      return options?.formatter ? options.formatter(time) : `Today at ${time}`
    }

    return formatAbsolute(date, 'MMM d, y', { locale })
  }

  if (format === 'relative-time') {
    return formatRelative(date, Date.now(), { locale })
  }

  return formatAbsolute(date, dateFormatMap[format], { locale })
}
